import { useCallback, useMemo } from 'react';
import type { Key } from 'react-aria-components';

import type { SelectSize } from '@/lib/base/forms/select/select';
import { Select } from '@/lib/base/forms/select/select';
import { COMMON_TIMEZONES, getTimezoneOffset } from '@/lib/scheduling/timezone';

export interface TimezonePickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  label: string;
  hint?: string;
  isDisabled?: boolean;
  size?: SelectSize;
}

/**
 * Labeled timezone field for the workspace default-timezone setting. Thin
 * wrapper over Select.ComboBox (like CurrencyPicker), listing COMMON_TIMEZONES
 * with each zone's current UTC offset as supporting text.
 */
export function TimezonePicker({
  value,
  onChange,
  label,
  hint,
  isDisabled = false,
  size = 'sm',
}: TimezonePickerProps) {
  const items = useMemo(
    () =>
      COMMON_TIMEZONES.map(tz => ({
        id: tz.value,
        label: tz.label,
        supportingText: getTimezoneOffset(tz.value),
      })),
    []
  );

  const handleSelectionChange = useCallback(
    (key: Key | null) =>
      onChange(key !== null && key !== undefined ? String(key) : null),
    [onChange]
  );

  return (
    <Select.ComboBox
      aria-label={label}
      label={label}
      hint={hint}
      placeholder='Select a timezone'
      size={size}
      items={items}
      selectedKey={value ?? null}
      onSelectionChange={handleSelectionChange}
      isDisabled={isDisabled}
      shortcut={false}
    >
      {item => (
        <Select.Item id={item.id} textValue={item.label}>
          {item.label}
          {item.supportingText && (
            <span className='text-text-tertiary ml-2 text-xs'>
              {item.supportingText}
            </span>
          )}
        </Select.Item>
      )}
    </Select.ComboBox>
  );
}
